import { useSelector } from 'react-redux';
import stateSocioeconomicSource from 'data/sources/stateSocioeconomicSource';
import { socioeconomicFactorRadius } from 'utils/socioeconomicScores';
import { IconLayer } from '@deck.gl/layers';
import { CollisionFilterExtension } from '@deck.gl/extensions';
import MapPointer from 'assets/img/map-pointer.png';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { LAYER_TYPE, isLayerVisible } from 'utils/layerVisibility';
import ReactDOMServer from 'react-dom/server';
import Popover from '../common/Popover';

export const STATE_SOCIOECONOMIC_LAYER_ID = 'stateSocioeconomicLayer';

export default function StateSocioeconomicLayer() {
  const [layer, setLayer] = useState(null);
  const [previousActiveState, setPreviousActiveState] = useState(null);
  const [previousActiveCompareState, setPreviousActiveCompareState] = useState(null);
  const [previousSocioeconomicFactor, setPreviousSocioeconomicFactor] = useState(null);
  const [previousYear, setPreviousYear] = useState(null);
  const { stateSocioeconomicLayer } = useSelector((state) => state.carto.layers);
  const source = stateSocioeconomicSource(
    stateSocioeconomicLayer?.year || 2019,
    stateSocioeconomicLayer?.socioeconomicFactor || 'overall_socioeconomic'
  );
  const viewState = useSelector((state) => state.carto.viewState);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!stateSocioeconomicLayer || !source) {
      return;
    }

    const isSocioeconomicFactorsLayerActive =
      searchParams.get('socioeconomic-layer') !== null
        ? searchParams.get('socioeconomic-layer') === 'true'
        : true;

    if (isSocioeconomicFactorsLayerActive === false) {
      setLayer(null);
      return;
    }

    if (isLayerVisible(LAYER_TYPE.STATE, viewState.zoom) === false) {
      setLayer(null);
      return;
    }

    if (
      layer &&
      previousActiveState === searchParams.get('active-state') &&
      previousActiveCompareState === searchParams.get('active-compare-state') &&
      previousSocioeconomicFactor === searchParams.get('socioeconomic-factor') &&
      previousYear === searchParams.get('year')
    ) {
      return;
    }

    if (
      stateSocioeconomicLayer?.data?.[0]?.[searchParams.get('socioeconomic-factor')] ===
        undefined ||
      stateSocioeconomicLayer?.data?.[0]?.year?.toString() !== searchParams.get('year')
    ) {
      return;
    }

    const newIconLayer = new IconLayer({
      id: STATE_SOCIOECONOMIC_LAYER_ID,
      data: stateSocioeconomicLayer.data,
      extensions: [new CollisionFilterExtension()],
      collisionTestProps: {
        sizeScale: 2,
      },
      pickable: true,
      iconAtlas: MapPointer,
      iconMapping: {
        marker: { x: 0, y: 0, width: 96, height: 97 },
      },
      getIcon: () => 'marker',
      sizeScale: 1,
      getSize: (object) => {
        let stateName = object.name;
        let isPercentage =
          stateSocioeconomicLayer.socioeconomicFactor.includes('percentage');
        let radius =
          isPercentage === true
            ? (object[stateSocioeconomicLayer.socioeconomicFactor] / 100) * 5
            : object[stateSocioeconomicLayer.socioeconomicFactor];

        if (
          searchParams.get('active-state') === stateName ||
          searchParams.get('active-compare-state') === stateName
        ) {
          return socioeconomicFactorRadius(radius);
        }

        return searchParams.get('active-state') === null
          ? socioeconomicFactorRadius(radius)
          : 0;
      },
      getPosition: (d) => [d.lng, d.lat],
      onHover: (info) => {
        if (info?.object) {
          let isPercentage =
            stateSocioeconomicLayer?.socioeconomicFactor.includes('percentage');

          info.object = {
            html: ReactDOMServer.renderToStaticMarkup(
              <Popover>
                <div className='whitespace-nowrap flex flex-col gap-1'>
                  <div className='text-sm'>
                    {stateSocioeconomicLayer.socioeconomicFactorLabel}:{' '}
                    <span className='text-blue-300'>
                      {Math.round(
                        info.object[stateSocioeconomicLayer.socioeconomicFactor] * 100
                      ) / 100}
                      {isPercentage === true ? '%' : ''}
                    </span>
                  </div>
                </div>
              </Popover>
            ),
            style: {
              background: 'transparent',
            },
          };
        }
      },
      updateTriggers: {
        getSize: [
          searchParams.get('active-state'),
          searchParams.get('active-compare-state'),
          searchParams.get('socioeconomic-factor'),
          searchParams.get('year'),
        ],
      },
    });

    setLayer(newIconLayer);
    setPreviousActiveState(searchParams.get('active-state'));
    setPreviousActiveCompareState(searchParams.get('active-compare-state'));
    setPreviousSocioeconomicFactor(searchParams.get('socioeconomic-factor'));
    setPreviousYear(searchParams.get('year'));
  }, [
    layer,
    previousActiveCompareState,
    previousActiveState,
    previousSocioeconomicFactor,
    previousYear,
    searchParams,
    source,
    stateSocioeconomicLayer,
    viewState.zoom,
  ]);

  return layer;
}
